import { Capacitor } from '@capacitor/core';
import {
  PURCHASE_EVIDENCE_IMAGE_EDGE,
  PURCHASE_EVIDENCE_MAX_BYTES,
  UPLOAD_RECOMMENDED_EDGE,
  UPLOAD_SETTINGS_COPY,
} from '@saesak/shared';
import { apiData, tokenStore } from './api';
import { getAppSettings } from './app-settings';

/**
 * 파일 업로드 공통 흐름 (§11) — 업로드 URL 발급 → 브라우저가 직접 PUT.
 * 앨범·서류 제출·급식 사진·원가(노래)·인스타 프레임·원아 사진이 같이 쓴다.
 * 앱 설정(v1.18)에 따라 올리기 전에 Wi-Fi 확인·사진 축소를 거친다.
 */
export async function uploadMediaAsset(
  file: File,
): Promise<{ mediaId: string; key: string }> {
  await confirmCellularUpload();
  file = await applyUploadQuality(file);
  return putMedia(file);
}

/**
 * 구매 증빙 업로드 (§5.29) — 앱 설정과 무관하게 사진은 긴 변 1600px로 줄인다.
 * 모델 입력 상한(PURCHASE_EVIDENCE_MAX_BYTES) 안에 들어오게 하고 데이터도 아낀다. PDF는 그대로.
 */
export async function uploadEvidence(file: File): Promise<{ mediaId: string; key: string }> {
  await confirmCellularUpload();
  const prepared = await shrinkImage(file, PURCHASE_EVIDENCE_IMAGE_EDGE);
  if (prepared.size > PURCHASE_EVIDENCE_MAX_BYTES) {
    throw new Error('증빙 파일이 너무 커요. 4MB 이하의 사진이나 PDF를 올려 주세요.');
  }
  return putMedia(prepared);
}

/** 업로드 URL 발급 → 직접 PUT — 공통 마무리 */
async function putMedia(file: File): Promise<{ mediaId: string; key: string }> {
  const upload = await apiData<{ mediaId: string; key: string; uploadUrl: string; method: string }>(
    '/media/upload-url',
    {
      method: 'POST',
      body: { filename: file.name, mimeType: file.type, sizeBytes: file.size },
    },
  );

  const headers: Record<string, string> = { 'Content-Type': file.type };
  // 로컬 스토리지 폴백은 API가 직접 받으므로(상대 경로) 인증이 필요하다.
  // 운영의 R2 presigned URL(절대 경로)에는 붙이지 않는다.
  if (upload.uploadUrl.startsWith('/')) {
    const token = tokenStore.get()?.accessToken;
    if (token) headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(upload.uploadUrl, {
    method: upload.method,
    headers,
    body: file,
  });
  if (!response.ok) throw new Error('파일을 올리지 못했어요. 잠시 후 다시 시도해 주세요.');
  return { mediaId: upload.mediaId, key: upload.key };
}

/** 앨범 등 mediaId만 필요한 호출부용 */
export async function uploadMedia(file: File): Promise<string> {
  return (await uploadMediaAsset(file)).mediaId;
}

/**
 * Wi-Fi 연결 확인 (v1.18) — 데이터(셀룰러)로 올릴 때 한 번 물어본다.
 * 네이티브는 Capacitor Network으로, 웹은 Network Information API가 있을 때만 판별한다.
 * 연결 종류를 알 수 없으면 막지 않는다 — 확인은 배려이지 장벽이 아니다.
 */
async function confirmCellularUpload(): Promise<void> {
  if (!getAppSettings().uploadWifiCheck) return;

  let isCellular = false;
  if (Capacitor.isNativePlatform()) {
    try {
      const { Network } = await import('@capacitor/network');
      isCellular = (await Network.getStatus()).connectionType === 'cellular';
    } catch {
      return;
    }
  } else {
    const connection = (navigator as { connection?: { type?: string; effectiveType?: string } })
      .connection;
    isCellular = connection?.type === 'cellular';
  }

  if (isCellular && !window.confirm(UPLOAD_SETTINGS_COPY.wifiConfirm)) {
    throw new Error('업로드를 취소했어요.');
  }
}

/**
 * 사진 화질 적용 (v1.18) — '권장'이면 긴 변 1920px로 줄여 데이터를 아낀다.
 * 캔버스가 못 읽는 형식(HEIC 등)이나 축소가 필요 없는 크기는 원본 그대로 둔다.
 */
async function applyUploadQuality(file: File): Promise<File> {
  if (getAppSettings().uploadQuality !== 'recommended') return file;
  return shrinkImage(file, UPLOAD_RECOMMENDED_EDGE);
}

/** 긴 변이 edge를 넘는 사진만 JPEG로 줄인다. 캔버스가 못 읽는 형식(HEIC 등)은 원본 그대로. */
async function shrinkImage(file: File, edge: number): Promise<File> {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return file;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return file;
  }

  const longest = Math.max(bitmap.width, bitmap.height);
  if (longest <= edge) {
    bitmap.close();
    return file;
  }

  const scale = edge / longest;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close();
    return file;
  }
  ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  if (!blob) return file;

  const name = file.name.replace(/\.(png|webp|jpeg|jpg)$/i, '') + '.jpg';
  return new File([blob], name, { type: 'image/jpeg' });
}
